import React, { useState, useEffect } from 'react';
import { getUserByExactUsername, getUserPhotos } from '../photoFunctions';
import { Gallery, styles } from '../components';
import SkeletonGallery from './SkeletonGallery';
import ErrorState from './ErrorState';
import { useNetworkStatus, isNetworkError, getErrorMessage } from '../utils/networkDetection';
import { supabase } from '../supabase';

// Guest Profile View - View-only profile page for Apple App Store compliance
export const GuestProfileView = ({ username, onBack }) => {
  const [guestUser, setGuestUser] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profileVisible, setProfileVisible] = useState(false);
  const [photosVisible, setPhotosVisible] = useState(false);
  const isOnline = useNetworkStatus();

  useEffect(() => {
    const loadGuestProfile = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Use username from props (URL param) or fallback to 'amishabansal' for backward compatibility
        const searchUsername = username || 'amishabansal';
        
        // OPTIMIZATION: Hardcoded fast path for amishabansal
        // User ID: c639a94e-a332-4ec7-b068-9178dfba8a2a
        const AMISHABANSAL_USER_ID = 'c639a94e-a332-4ec7-b068-9178dfba8a2a';
        
        let foundUser;
        let userId;
        
        // Ultra-fast path: Direct query by ID for amishabansal (skips username lookup)
        if (searchUsername.toLowerCase() === 'amishabansal') {
          userId = AMISHABANSAL_USER_ID;
          
          // FIRST: Fetch profile immediately and show it (don't wait for photos)
          const profileResult = await supabase
            .from('profiles')
            .select('id, username, name, avatar, bio')
            .eq('id', AMISHABANSAL_USER_ID)
            .single();
          
          if (!profileResult.error && profileResult.data) {
            foundUser = profileResult.data;
            // Show profile IMMEDIATELY - don't wait for photos
            setGuestUser(foundUser);
            setLoading(false); // User can see the profile now
            // Trigger fade-in animation after a tiny delay
            setTimeout(() => setProfileVisible(true), 50);
          } else {
            setError(new Error('this username does not exist :('));
            setLoading(false);
            return;
          }
          
          // THEN: Load photos in the background (non-blocking)
          // This happens after the profile is already shown
          supabase
            .from('photos')
            .select('*')
            .eq('user_id', AMISHABANSAL_USER_ID)
            .order('created_at', { ascending: false })
            .then((photosResult) => {
              if (!photosResult.error && photosResult.data) {
                setPhotos(photosResult.data);
                // Trigger fade-in animation for photos (but enable pointer events immediately)
                setPhotosVisible(true);
              } else if (photosResult.error) {
                console.warn('Error loading photos:', photosResult.error);
              }
            })
            .catch((err) => {
              console.warn('Error loading photos:', err);
            });
          
          return;
        }
        
        // Fallback to exact username lookup for other usernames
        foundUser = await getUserByExactUsername(searchUsername, null);
        
        if (foundUser) {
          userId = foundUser.id;
          setGuestUser(foundUser);
          setTimeout(() => setProfileVisible(true), 50);
          
          // Load photos (no currentUserId since guest)
          const userPhotos = await getUserPhotos(foundUser.id, null);
          setPhotos(userPhotos);
          // Enable pointer events immediately when photos are loaded
          setPhotosVisible(true);
        } else {
          setError(new Error('this username does not exist :('));
        }
      } catch (err) {
        console.error('Error loading guest profile:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    loadGuestProfile();
  }, [username]); // Re-run when username changes

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#a2a9ac',
      paddingTop: 'env(safe-area-inset-top)',
      paddingBottom: 'env(safe-area-inset-bottom)',
      paddingLeft: 'env(safe-area-inset-left)',
      paddingRight: 'env(safe-area-inset-right)'
    }}>
      <style>{styles}</style>
      
      {/* Header */}
      <header style={{
        textAlign: 'center',
        padding: 'clamp(40px, 8vw, 60px) clamp(20px, 5vw, 40px) clamp(20px, 5vw, 30px)',
        background: 'linear-gradient(180deg, #a2a9ac 0%, rgba(162,169,172,0) 100%)',
        position: 'relative',
        opacity: profileVisible ? 1 : 0,
        transform: profileVisible ? 'translateY(0)' : 'translateY(10px)',
        transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
      }}>
        {/* Back Button */}
        <button
          onClick={onBack}
          style={{
            position: 'absolute',
            top: 'calc(30px + env(safe-area-inset-top))',
            left: 'calc(30px + env(safe-area-inset-left))',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 'clamp(8px, 2vw, 12px)',
            minWidth: '44px',
            minHeight: '44px',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0.8,
            borderRadius: '8px',
            WebkitTapHighlightColor: 'transparent',
            zIndex: 1000
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.opacity = '1';
            e.currentTarget.style.transform = 'scale(1.1)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.opacity = '0.8';
            e.currentTarget.style.transform = 'scale(1)';
          }}
          title="Back"
        >
          <svg 
            width="clamp(24px, 6vw, 32px)" 
            height="clamp(24px, 6vw, 32px)" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="#312310" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            style={{
              filter: 'drop-shadow(0 1px 2px rgba(49,35,16,0.3))'
            }}
          >
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>

        {/* Profile Picture & Username Container */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'clamp(8px, 2vw, 16px)'
        }}>
          {/* Profile Picture */}
          <div style={{
            width: 'clamp(80px, 20vw, 120px)',
            height: 'clamp(80px, 20vw, 120px)',
            borderRadius: '50%',
            background: guestUser?.avatar 
              ? `url(${guestUser.avatar}) center/cover` 
              : '#c6a25b',
            border: '4px solid #af731f',
            boxShadow: '0 4px 15px rgba(49,35,16,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 'clamp(2em, 8vw, 4em)',
            color: '#312310',
            fontFamily: "'Apple Garamond', Garamond, 'Times New Roman', serif",
            fontWeight: 400,
            flexShrink: 0
          }}>
            {!guestUser?.avatar && (guestUser?.username?.[0] || 'U').toUpperCase()}
          </div>

          {/* Username */}
          <h1 style={{
            fontFamily: "'Apple Garamond', Garamond, 'Times New Roman', serif",
            fontSize: 'clamp(2em, 8vw, 5em)',
            color: '#312310',
            letterSpacing: '0.08em',
            fontWeight: 400,
            marginBottom: '0',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            maxWidth: '100%',
            padding: '0 20px',
            boxSizing: 'border-box'
          }}>
            {error && !guestUser ? (username || 'User') : (guestUser?.username || '')}
          </h1>
        </div>

        <p style={{
          fontFamily: "'Montserrat', sans-serif",
          fontSize: 'clamp(0.75em, 3vw, 0.85em)',
          letterSpacing: '0.02em',
          color: '#553918',
          fontWeight: 300,
          marginTop: 'clamp(4px, 1vw, 8px)',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          maxWidth: '100%',
          padding: '0 20px',
          boxSizing: 'border-box',
          lineHeight: '1.5'
        }}>
          {error && !guestUser ? 'this username does not exist :(' : (guestUser?.bio || 'img archive')}
        </p>
        
        <div style={{
          width: '60px',
          height: '1px',
          background: '#af731f',
          margin: 'clamp(10px, 2.5vw, 20px) auto 0'
        }}></div>
      </header>

      {/* Gallery */}
      <div style={{
        maxWidth: '1600px',
        margin: '0 auto',
        padding: '0 clamp(20px, 5vw, 40px) clamp(40px, 10vw, 80px)',
        paddingBottom: 'calc(clamp(40px, 10vw, 80px) + env(safe-area-inset-bottom))',
        WebkitOverflowScrolling: 'touch'
      }}>
        {error && photos.length === 0 ? (
          <ErrorState 
            onRetry={() => window.location.reload()}
            message={getErrorMessage(error)}
          />
        ) : photos.length > 0 ? (
          <Gallery 
            photos={photos} 
            onDelete={null}
            canDelete={false}
          />
        ) : null}
      </div>
    </div>
  );
};

